'use client';

import {
  Box,
  SxProps,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import {
  JSXConvertersFunction,
  RichText as PayloadRichText,
} from '@payloadcms/richtext-lexical/react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import getConverters from './converters';
import { DeepNullToUndefined } from '@jd/client/utils/unnulable';
import { useSectionContext } from '../sections/utils/SectionContext';

type EditorProps = {
  children?: DeepNullToUndefined<SerializedEditorState>;
  withTypography?: false;
  sx?: SxProps;
};

type RichTypographyProps = Omit<TypographyProps, 'children'> & {
  children?: DeepNullToUndefined<SerializedEditorState>;
  withTypography: true;
};

export type RichTextProps = EditorProps | RichTypographyProps;
export type RichTextContent = RichTextProps['children'];

const getInlineConverters =
  (baseConverters: JSXConvertersFunction): JSXConvertersFunction =>
  ({ defaultConverters }) => ({
    ...baseConverters({ defaultConverters }),
    paragraph: ({ node, nodesToJSX }) => (
      <span key={node.format}>{nodesToJSX({ nodes: node.children })}</span>
    ),
    heading: ({ node, nodesToJSX }) => (
      <span key={node.format}>{nodesToJSX({ nodes: node.children })}</span>
    ),
    quote: ({ node, nodesToJSX }) => (
      <span key={node.format}>{nodesToJSX({ nodes: node.children })}</span>
    ),
  });

const RichText = (props: RichTextProps) => {
  const theme = useTheme();
  const { config } = useSectionContext();

  if (!props.children) return null;

  const lexicalContent = (
    <Box sx={props.sx}>
      <PayloadRichText
        data={props.children as SerializedEditorState}
        converters={getConverters(theme.palette, config)}
      />
    </Box>
  );

  if (props.withTypography) {
    const { children, withTypography, sx, ...rest } = props;

    return (
      <Typography
        {...rest}
        sx={{
          '& p': { m: 0 },
          '& *': {
            fontSize: 'inherit',
            fontFamily: 'inherit',
            lineHeight: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
          },
          ...sx,
        }}
      >
        <PayloadRichText
          disableContainer
          data={children as SerializedEditorState}
          converters={getInlineConverters(getConverters(theme.palette, config))}
        />
      </Typography>
    );
  }

  return lexicalContent;
};

export default RichText;
